/* import __COLOCATED_TEMPLATE__ from './metrics-table.hbs'; */
/* RESPONSIBLE TEAM: team-ai-insights */
import Component from '@glimmer/component';
import { use } from 'ember-resources/util/function-resource';
import type { ReportState } from 'embercom/components/reporting/custom/report/chart-card';
import { groupBy } from 'underscore';
import { staticDataResource } from 'embercom/lib/reporting/static-data-resource';
import { inject as service } from '@ember/service';
import type ReportingMetrics from 'embercom/services/reporting-metrics';
import { action } from '@ember/object';
import { cached } from 'tracked-toolbox';
import type {
  Job,
  JobId,
} from 'embercom/components/reporting/automation/ai-insights/fin-insights/jobs';
import type IntlService from 'ember-intl/services/intl';
import { tracked } from '@glimmer/tracking';
import { TrackedObject } from 'tracked-built-ins';
import { type MetricValue } from 'embercom/lib/reporting/chart-data-resource';
import { isPresent } from '@ember/utils';
import { and } from 'embercom/components/reporting/automation/ai-insights/fin-insights/sankey-config';
import { type Filter } from 'embercom/objects/reporting/unified/metrics/types';
import type { ChartSeriesDefinition } from 'embercom/components/reporting/automation/ai-insights/fin-insights/jobs';
import type ContentSuggestionsService from 'embercom/services/content-suggestions-service';
import { taskFor } from 'ember-concurrency-ts';

export type MetricTableJob = Pick<Job, 'showSuggestions' | 'table'> & { id: 'metrics-table' };
interface Signature {
  Args: {
    selectedJob: Job | MetricTableJob;
    reportState: ReportState;
    onShowConversationExplorer: (
      topic: string,
      subtopic: string | null,
      type: 'suggestions' | 'conversations',
      conversationCount: number,
    ) => void;
    trackAnalyticsEvent: (
      action: string,
      object: string,
      extraContext: Record<string, any>,
    ) => void;
    hideRowsWithoutSuggestions: boolean;
    toggleHideRowsWithoutSuggestions: () => void;
  };
}

interface SortState {
  columnIndex: number;
  direction: 'asc' | 'desc';
}

interface RowData {
  key: string;
  metricValues: MetricValue[];
}

interface TopicRowData extends RowData {
  subtopicData: {
    topicId: string;
    key: string;
    metricValues: MetricValue[];
  }[];
}

interface Column {
  label: string;
  tooltip?: string;
  type: 'key' | 'metric' | 'suggestion';
}

export default class MetricsTable extends Component<Signature> {
  @service declare reportingMetrics: ReportingMetrics;
  @service declare intl: IntlService;
  @service declare contentSuggestionsService: ContentSuggestionsService;

  @use topicsData = staticDataResource(this, {
    serializedChart: () => this.topicsChartDefinition,
    reportState: () => this.args.reportState,
  });
  @use subtopicsData = staticDataResource(this, {
    serializedChart: () => this.subtopicsChartDefinition,
    reportState: () => this.args.reportState,
  });

  sortStates = new TrackedObject<Record<JobId | MetricTableJob['id'], SortState>>();

  @tracked expandedTopicId: string | null = null;

  get metricStartIndex() {
    return this.args.selectedJob.showSuggestions ? 2 : 1;
  }

  get topicsChartDefinition() {
    return {
      view_by: 'conversation_custom_fields#ai_insights_topic',
      view_by_display_limit: 5000,
      view_by_other: false,
      chart_series: this.args.selectedJob.table.chartSeries.map(
        (series: ChartSeriesDefinition) => ({
          ...series,
          filters: and(
            this.args.selectedJob.table.filters,
            series.filters,
            this.conversationStartedAtFilter(series),
          ),
        }),
      ),
      visualization_type: 'bespoke',
    };
  }

  // Creates a date range filter for the chart series restricting the date range to the
  // first_user_conversation_part_created_at, for metrics that don't already use it, but do support it.
  conversationStartedAtFilter(series: ChartSeriesDefinition): Filter | null {
    let metric = this.reportingMetrics.getMetricById(series.metric_id);
    let attributes = metric.attributesForDataset;
    let timeAttribute = attributes.find(
      (a) => a.id === 'conversation.first_user_conversation_part_created_at',
    );
    if (timeAttribute && metric.timeProperty !== timeAttribute.field) {
      let dateRange = this.args.reportState.dateRange;
      return {
        type: 'date_range',
        data: {
          attribute: timeAttribute.id,
          property: timeAttribute.field,
          values: [dateRange.startMoment.valueOf(), dateRange.endMoment.valueOf()],
        },
      };
    } else {
      return null;
    }
  }

  get subtopicsChartDefinition() {
    return {
      ...this.topicsChartDefinition,
      view_by: 'conversation_custom_fields#ai_insights_topic_subtopic',
    };
  }

  get metrics() {
    return this.args.selectedJob.table.chartSeries.map((series) =>
      this.reportingMetrics.getMetricById(series.metric_id),
    );
  }

  get headerColumns(): Column[] {
    let columns: Column[] = [
      {
        label: this.intl.t(
          'components.reporting.automation.ai-insights.fin-insights.metrics-table.topic-header',
        ),
        type: 'key',
      },
      {
        label: this.intl.t(
          'components.reporting.automation.ai-insights.fin-insights.metrics-table.suggestions-header',
        ),
        type: 'suggestion',
      },
      ...this.args.selectedJob.table.chartSeries.map((series, index) => ({
        label: series.label || this.metrics[index].name,
        type: 'metric' as const,
      })),
    ];

    if (this.args.selectedJob.showSuggestions) {
      return columns;
    } else {
      return columns.filter((column) => column.type !== 'suggestion');
    }
  }

  get rows(): TopicRowData[] {
    let subTopicDataArray = Object.entries(this.subtopicsData.flatCombinedData).map(
      ([key, metricValues]) => {
        let [topicId, subtopicId] = key.split('=>');
        return {
          topicId,
          key: subtopicId,
          metricValues,
        };
      },
    );
    let subtopicData = groupBy(subTopicDataArray, 'topicId');
    let conversationCountIndex = this.args.selectedJob.table.chartSeries.findIndex(
      (series) => series.metric_id === 'v1.new_conversations',
    );
    return Object.entries(this.topicsData.flatCombinedData)
      .filter(([_key, metricValues]) => {
        return this.numericValue(metricValues[conversationCountIndex])! > 0;
      })
      .map(([key, metricValues]) => {
        return { key, metricValues, subtopicData: subtopicData[key] ?? [] };
      });
  }

  @cached
  get highlightedMetricMinMaxValues() {
    let allValues = Object.entries(this.topicsData.flatCombinedData).map(([_key, metricValues]) => {
      let metricValue = metricValues[this.args.selectedJob.table.highlightedMetricIndex];
      return this.numericValue(metricValue) ?? 0;
    });
    let minValue = Math.min(...allValues);
    let maxValue = Math.max(...allValues);
    return { minValue, maxValue };
  }

  get sortState() {
    return (
      this.sortStates[this.args.selectedJob.id] || {
        columnIndex: this.args.selectedJob.table.highlightedMetricIndex + this.metricStartIndex,
        direction: 'desc',
      }
    );
  }

  set sortState(sortState: SortState) {
    this.sortStates[this.args.selectedJob.id] = sortState;
  }

  @cached
  get sortedRows() {
    let columnIndex = this.sortState.columnIndex;
    let data = [...this.rows];
    let sortAscending = this.sortState.direction === 'asc';
    let sortFunction = (a: any, b: any) => {
      if (columnIndex === 0) {
        // sorting by topic or subtopic name
        return sortAscending ? a.key.localeCompare(b.key) : b.key.localeCompare(a.key);
      } else if (this.args.selectedJob.showSuggestions && columnIndex === 1) {
        // TODO implement sorting suggestions?
        return 0;
      } else {
        // sorting by a metric value
        let aValue = this.valueForSorting(a.metricValues[columnIndex - this.metricStartIndex]);
        let bValue = this.valueForSorting(b.metricValues[columnIndex - this.metricStartIndex]);
        return sortAscending ? aValue - bValue : bValue - aValue;
      }
    };
    data.sort(sortFunction);
    // sort the subtopicData for each topic
    data.forEach((row) => {
      row.subtopicData?.sort(sortFunction);
      if (this.args.hideRowsWithoutSuggestions) {
        row.subtopicData = row.subtopicData?.filter(
          (subtopic) => this.getSuggestionCountForTopic(row.key, subtopic.key) > 0,
        );
      }
    });

    if (this.args.hideRowsWithoutSuggestions) {
      data = data.filter((row) => this.getSuggestionCountForTopic(row.key) > 0);
    }
    return data;
  }

  valueForSorting(value: MetricValue): number {
    if (typeof value === 'number') {
      return value;
    } else if (isPresent(value) && isPresent(value.value)) {
      return value.value!;
    } else {
      // sort null values to the bottom
      return -Number.MAX_VALUE;
    }
  }

  @action
  onSort(columnIndex: number) {
    let direction: SortState['direction'] = 'desc';
    if (this.sortState.columnIndex === columnIndex) {
      // toggle the direction
      direction = this.sortState.direction === 'asc' ? 'desc' : 'asc';
    }

    this.sortState = {
      columnIndex,
      direction,
    };
    this.args.trackAnalyticsEvent('sort-changed', 'metrics-table', {
      columnIndex,
      direction,
    });
  }

  get isLoading() {
    // resources are lazily loaded, so this next bit is a hack to make sure that
    // we eagerly get the subtopic data without waiting for the topics rows to be rendered.
    this.subtopicsData.isLoading;
    this.topicsData.isLoading;
    return this.subtopicsData.isLoading || this.topicsData.isLoading;
  }

  @action
  expandCollapseRow(topicId: string) {
    this.expandedTopicId = this.expandedTopicId === topicId ? null : topicId;
    this.args.trackAnalyticsEvent('row-expanded', 'metrics-table', {
      topicId,
    });
  }

  @action
  formatMetricValue(chartSeries: ChartSeriesDefinition, rawValue: MetricValue) {
    return this.topicsData.formatValue({
      value: rawValue,
      metricId: chartSeries.metric_id,
      aggregation: chartSeries.aggregation,
      durationFormat: 'hours',
    });
  }

  numericValue(value: MetricValue | null): number | null {
    if (typeof value === 'number') {
      return value;
    }
    return value?.value ?? null;
  }

  @action
  onExploreConversations(topicRow: RowData, subtopicRow: RowData | null) {
    let rowData = subtopicRow ?? topicRow;
    this.args.onShowConversationExplorer(
      topicRow.key,
      subtopicRow?.key ?? null,
      'conversations',
      this.numericValue(rowData.metricValues[0]) ?? 0,
    );
  }

  @action
  onExploreSuggestions(topicRow: RowData, subtopicRow: RowData | null) {
    taskFor(this.contentSuggestionsService.loadSuggestionsForTopic).perform(
      topicRow.key,
      subtopicRow?.key ?? null,
    );

    let rowData = subtopicRow ?? topicRow;
    this.args.onShowConversationExplorer(
      topicRow.key,
      subtopicRow?.key ?? null,
      'suggestions',
      this.numericValue(rowData.metricValues[0]) ?? 0,
    );
  }

  get shouldShowEmptyState() {
    return !this.isLoading && this.sortedRows.length === 0;
  }

  get emptyStateHeader() {
    if (this.args.hideRowsWithoutSuggestions) {
      return this.intl.t(
        'components.reporting.automation.ai-insights.fin-insights.metrics-table.empty-state-header-hide-rows-without-suggestions',
      );
    } else {
      return this.intl.t(
        'components.reporting.automation.ai-insights.fin-insights.metrics-table.no-data-title',
      );
    }
  }

  get emptyStateDescription() {
    if (this.args.hideRowsWithoutSuggestions) {
      return this.intl.t(
        'components.reporting.automation.ai-insights.fin-insights.metrics-table.empty-state-description-hide-rows-without-suggestions',
      );
    } else {
      return this.intl.t(
        'components.reporting.automation.ai-insights.fin-insights.metrics-table.no-data-description',
      );
    }
  }

  getSuggestionCountForTopic(topicId: string, subtopicId: string | null = null) {
    return this.contentSuggestionsService.getTopicsSuggestionCount(topicId, subtopicId);
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Reporting::Automation::AiInsights::FinInsights::MetricsTable': typeof MetricsTable;
  }
}
